import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Search, ChevronDown, Check } from 'lucide-react';

export const Card = ({ children, className = '' }) => (
  <div className={`bg-white border border-slate-100 rounded-xl ${className}`}>
    {children}
  </div>
);

export const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  className = '',
  isLoading = false,
  ...props
}) => {
  const variants = {
    primary: 'bg-primary text-white hover:bg-primary-dark transition-all active:scale-95',
    secondary: 'bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors',
    danger: 'bg-red-500 text-white hover:bg-red-600 transition-colors',
    outline: 'border border-slate-200 text-slate-600 hover:border-primary hover:text-primary transition-all duration-200',
    ghost: 'text-slate-500 hover:bg-slate-100 transition-colors'

  };

  const sizes = {
    sm: 'px-3 py-1.5 text-xs font-semibold',
    md: 'px-4 py-2 text-sm font-bold',
    lg: 'px-6 py-3 text-base font-bold'
  };

  return (
    <button
      disabled={isLoading}
      className={`inline-flex items-center justify-center gap-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
    >

      {isLoading && <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
      {children}
    </button>
  );
};

export const Input = ({ label, error, icon: Icon, className = '', ...props }) => (
  <div className="space-y-1.5 w-full">
    {label && <label className="text-xs  text-slate-800 ml-1">{label}</label>}
    <div className="relative group">
      {Icon && (
        <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-primary transition-colors">
          <Icon className="w-5 h-5" />
        </div>
      )}
      <input
        className={`w-full bg-slate-50/50 border  text-xs border-slate-200 text-slate-800 rounded-sm py-2.5 ${Icon ? 'pr-10' : 'pr-4'} pl-4 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all placeholder:text-slate-400 ${className}`}
        {...props}
      />

    </div>
    {error && <p className="text-xs text-red-500 font-medium mr-1">{error}</p>}
  </div>
);

export const Select = ({ label, error, options = [], className = '', value, onChange, placeholder = 'اختر خياراً...', disabled = false, ...props }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPos, setDropdownPos] = useState({});
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const lastWidth = useRef(window.innerWidth);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isInsideContainer = containerRef.current && containerRef.current.contains(event.target);
      const isInsideDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);
      
      if (!isInsideContainer && !isInsideDropdown) {
        setIsOpen(false);
      }
    };

    const handleResize = () => {
      if (window.innerWidth !== lastWidth.current) {
        lastWidth.current = window.innerWidth;
        setIsOpen(false);
      }
    };

    if (isOpen) {
      lastWidth.current = window.innerWidth; // Reset on open
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleClickOutside, true);
      window.addEventListener('resize', handleResize);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleClickOutside, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    return options.filter(opt =>
      String(opt.label || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [options, searchTerm]);

  const selectedOption = options.find(opt => String(opt.value) === String(value));

  const handleSelect = (val) => {
    if (disabled) return;
    if (onChange) {
      // Create a mock event for compatibility with existing onChange handlers
      const event = {
        target: {
          value: val,
          name: props.name
        }
      };
      onChange(event);
    }
    setIsOpen(false);
    setSearchTerm('');
  };

  const toggleOpen = () => {
    if (disabled) return;
    if (!isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 300; // Approximate max height

      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setDropdownPos({
          position: 'fixed',
          bottom: window.innerHeight - rect.top + 4,
          left: rect.left,
          width: rect.width,
          zIndex: 99999
        });
      } else {
        setDropdownPos({
          position: 'fixed',
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width,
          zIndex: 99999
        });
      }
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  return (
    <div className={`space-y-1.5 w-full relative ${disabled ? 'opacity-70' : ''}`} ref={containerRef}>
      {label && <label className="text-xs  text-slate-800 ml-1">{label}</label>}

      <div
        onClick={toggleOpen}
        className={`w-full bg-slate-50/50 border border-slate-200 text-slate-800 rounded-lg py-2.5 px-4 flex items-center justify-between transition-all ${disabled ? 'cursor-not-allowed bg-slate-100' : 'cursor-pointer focus:ring-2 focus:ring-primary/40 focus:border-primary'} ${isOpen ? 'ring-2 ring-primary/40 border-primary shadow-sm' : ''} ${className}`}
      >
        <span className={`text-xs ${!selectedOption ? 'text-slate-400' : 'text-slate-700 font-medium'}`}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180 text-primary' : ''}`} />
      </div>

      {isOpen && createPortal(
        <div ref={dropdownRef} style={dropdownPos} className="bg-white border border-slate-100 rounded-xl shadow-2xl shadow-slate-200/60 overflow-hidden animate-in fade-in zoom-in-95 duration-200 mt-1">
          <div className="p-2 border-b border-slate-50 bg-slate-50/30">
            <div className="relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                autoFocus
                className="w-full bg-white border border-slate-200 rounded-lg py-2 pr-9 pl-4 text-sm focus:outline-none focus:border-primary transition-all text-right"
                placeholder="ابحث هنا..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto custom-scrollbar">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <div
                  key={opt.value}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(opt.value);
                  }}
                  className={`px-4 py-2.5 text-xs cursor-pointer flex items-center justify-between group transition-colors ${String(opt.value) === String(value)
                      ? 'bg-primary/5 text-primary font-bold'
                      : 'text-slate-600 hover:bg-slate-50'
                    }`}
                >
                  <span className="flex-1 text-right">{opt.label}</span>
                  {String(opt.value) === String(value) && <Check className="w-4 h-4 mr-2" />}
                </div>
              ))
            ) : (
              <div className="px-4 py-6 text-center text-slate-400 text-sm">
                لا توجد نتائج
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {error && <p className="text-xs text-red-500 font-medium mr-1">{error}</p>}
    </div>
  );
};

export const Badge = ({ children, variant = 'neutral' }) => {
  const variants = {
    success: 'bg-green-100 text-green-700',
    danger: 'bg-red-100 text-red-700',
    warning: 'bg-amber-100 text-amber-700',
    primary: 'bg-orange-100 text-primary',
    neutral: 'bg-slate-100 text-slate-600'
  };
  return (
    <span className={`px-3 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${variants[variant]}`}>
      {children}
    </span>

  );
};
